import React, { useMemo, useState, useRef, type ReactNode } from 'react';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { ActionName, type ActionItem } from '@atlaskit/smart-card';
import { useIntl } from '@atlassian/jira-intl';
import { PopupTrigger } from '@atlassian/entry-point-popup';

import { useProjectUrlConstructor } from '../../util/index.tsx';
import { inheritancePopupEntryPoint } from '../inheritance-popup/entrypoint.tsx';
import { InheritancePopupSkeleton } from '../inheritance-popup/fallbacks/skeleton.tsx';
import { InheritancePopupErrorFallback } from '../inheritance-popup/fallbacks/error-fallback.tsx';
import { SimpleInlineSmartLink } from './inline-smart-llink/index.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	container: {
		paddingInlineEnd: token('space.100'),
	},
});

export const EntryPointWrapper = ({
	children,
	onClose,
	onInheritancePopupOpen,
}: {
	children: ReactNode;
	onClose?: () => void;
	onInheritancePopupOpen?: () => void;
}) => {
	return (
		<PopupTrigger
			entryPoint={inheritancePopupEntryPoint}
			fallback={<InheritancePopupSkeleton />}
			errorFallback={InheritancePopupErrorFallback}
			entryPointProps={{ onInheritancePopupOpen }}
			onClose={onClose}
			trigger={(triggerProps) => (
				<Box
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...triggerProps}
				>
					{children}
				</Box>
			)}
			placement="bottom"
		/>
	);
};

type AtlasProjectSmartLinkProps = {
	projectAri: string;
	projectKey?: string | null;
	name?: string | null;
	isPrivate: boolean;
	isInherited: boolean;
};

export const AtlasProjectSmartLink = ({
	projectAri,
	projectKey,
	name,
	isPrivate,
	isInherited,
}: AtlasProjectSmartLinkProps) => {
	const { formatMessage } = useIntl();
	const constructAtlasProjectUrl = useProjectUrlConstructor();
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [isPinned, setIsPinned] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const shouldShowActions = isInherited && (isHovered || isFocused || isPinned);

	const actions: ActionItem[] | undefined = useMemo(() => {
		return [
			{
				name: ActionName.CustomAction,
				onClick: () => undefined,
				content: formatMessage(messages.inheritedProject),
				tooltipMessage: formatMessage(messages.inheritedProjectTooltip),
				hideIcon: true,
				entryPointWrapper: (p) => (
					<EntryPointWrapper
						{...p}
						onClose={() => setIsPinned(false)}
						onInheritancePopupOpen={() => setIsPinned(true)}
					/>
				),
			},
		];
	}, [formatMessage]);

	// @TODO: handle private project
	// https://jplat.atlassian.net/browse/ANIP-121
	if (isPrivate) {
		return formatMessage(messages.privateProject);
	}

	if (projectKey) {
		const url = constructAtlasProjectUrl({ ari: projectAri, projectKey });
		return (
			<Box
				ref={containerRef}
				testId="issue-field-atlas-project-common.ui.atlas-project-smart-link.container"
				xcss={!isInherited && styles.container}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onMouseDown={() => {
					// this is covering an edge case where user clicks inherit button to close the popup, it won't lose focus so we have to help it a little
					if (isPinned) {
						setIsFocused(false);
					}
				}}
				onFocus={() => setIsFocused(true)}
				onBlur={(event: React.FocusEvent) => {
					// Check if focus is moving outside the container
					if (containerRef.current && !containerRef.current.contains(event.relatedTarget)) {
						setIsFocused(false);
					}
				}}
			>
				<SimpleInlineSmartLink
					anchorTarget="_blank"
					url={url}
					metadata={[]}
					actions={shouldShowActions ? actions : undefined}
				/>
			</Box>
		);
	}

	return <>{name}</>;
};
